import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight, ChevronLeft, Check, Sparkles, DollarSign, Building2, BarChart3, Rocket, ClipboardList, Newspaper, FolderOpen, Bot, PlusCircle, Banknote, TrendingUp, Zap, Calculator, Users, Tent, LifeBuoy, PartyPopper } from 'lucide-react';
import { useLanguage } from './LanguageContext';

// ============================================
// WALKTHROUGH HOOK
// ============================================

export const useWalkthrough = () => {
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  
  const openWalkthrough = useCallback(() => setShowWalkthrough(true), []);
  const closeWalkthrough = useCallback(() => setShowWalkthrough(false), []);
  const completeWalkthrough = useCallback(() => {
    setShowWalkthrough(false);
    localStorage.setItem('walkthrough_completed', 'true');
  }, []);
  
  return { showWalkthrough, openWalkthrough, closeWalkthrough, completeWalkthrough };
};

// ============================================
// DETAILED TOUR STEPS WITH ELEMENT TARGETING
// ============================================

interface TourStep {
  id: string;
  targetTab?: string;
  targetSelector?: string;  // CSS selector for element to highlight
  title: string;
  titleDe?: string;
  description: string;
  descriptionDe?: string;
  icon?: React.ReactNode;
  position?: 'top' | 'bottom' | 'auto';  // Where to show tooltip
}

export const tourSteps: TourStep[] = [
  // ===== WELCOME =====
  {
    id: 'welcome',
    title: 'Welcome to Asset Desk',
    titleDe: 'Willkommen bei Asset Desk',
    description: 'Your exclusive Dubai real estate investment portal. Let us guide you through the key features.',
    descriptionDe: 'Ihr exklusives Dubai Immobilien-Investitionsportal. Lassen Sie uns die wichtigsten Funktionen zeigen.',
    icon: <Sparkles size={24} color="#D6B160" />
  },

  // ===== DASHBOARD TAB =====
  {
    id: 'dashboard-portfolio',
    targetTab: 'dashboard',
    targetSelector: '.portfolio-card-mini',
    title: 'Portfolio Value',
    titleDe: 'Portfolio-Wert',
    description: 'Your total investment value at a glance. Tap to see detailed portfolio analytics.',
    descriptionDe: 'Ihr Gesamtinvestitionswert auf einen Blick. Tippen Sie für detaillierte Portfolio-Analysen.',
    icon: <DollarSign size={24} color="#D6B160" />,
    position: 'bottom'
  },
  {
    id: 'dashboard-properties',
    targetTab: 'dashboard',
    targetSelector: '.properties-section',
    title: 'My Properties',
    titleDe: 'Meine Immobilien',
    description: 'All your Dubai properties with purchase details, area, price per sqft, and current value.',
    descriptionDe: 'Alle Ihre Dubai-Immobilien mit Kaufdetails, Fläche, Preis pro sqft und aktuellem Wert.',
    icon: <Building2 size={24} color="#D6B160" />,
    position: 'bottom'
  },
  {
    id: 'dashboard-invest',
    targetTab: 'dashboard',
    targetSelector: '.hero-banner',
    title: 'Investment Opportunities',
    titleDe: 'Investitionsmöglichkeiten',
    description: 'Discover exclusive off-plan projects in Dubai. Tap to explore new investment opportunities.',
    descriptionDe: 'Entdecken Sie exklusive Off-Plan-Projekte in Dubai. Tippen Sie für neue Investitionsmöglichkeiten.',
    icon: <Rocket size={24} color="#D6B160" />,
    position: 'top'
  },
  {
    id: 'dashboard-requests',
    targetTab: 'dashboard',
    targetSelector: '.request-row',
    title: 'Quick Requests',
    titleDe: 'Schnellanfragen',
    description: 'Request documents, service support, contact your advisor, or suggest new features.',
    descriptionDe: 'Dokumente anfordern, Service-Support, Berater kontaktieren oder neue Features vorschlagen.',
    icon: <ClipboardList size={24} color="#D6B160" />,
    position: 'top'
  },
  {
    id: 'dashboard-news',
    targetTab: 'dashboard',
    targetSelector: '.news-list',
    title: 'Dubai Market News',
    titleDe: 'Dubai Marktnachrichten',
    description: 'Stay updated with the latest Dubai real estate market news and property trends.',
    descriptionDe: 'Bleiben Sie auf dem Laufenden mit den neuesten Dubai Immobilienmarkt-Nachrichten.',
    icon: <Newspaper size={24} color="#D6B160" />,
    position: 'top'
  },
  {
    id: 'dashboard-projects',
    targetTab: 'dashboard',
    targetSelector: '.projects-carousel',
    title: 'Upcoming Projects',
    titleDe: 'Kommende Projekte',
    description: 'Exclusive preview of upcoming Dubai developments. Get early access to premium launches.',
    descriptionDe: 'Exklusive Vorschau auf kommende Dubai-Entwicklungen. Früher Zugang zu Premium-Starts.',
    icon: <Building2 size={24} color="#D6B160" />,
    position: 'top'
  },

  // ===== PROPERTIES TAB =====
  {
    id: 'properties-intro',
    targetTab: 'properties',
    title: 'Properties Management',
    titleDe: 'Immobilienverwaltung',
    description: 'Your complete property portfolio. Add, manage, and track all your Dubai investments.',
    descriptionDe: 'Ihr komplettes Immobilienportfolio. Fügen Sie hinzu, verwalten und verfolgen Sie alle Investments.',
    icon: <FolderOpen size={24} color="#D6B160" />
  },
  {
    id: 'properties-upload',
    targetTab: 'properties',
    targetSelector: '.upload-section, .document-upload, [class*="upload"]',
    title: 'AI Document Processing',
    titleDe: 'KI-Dokumentenverarbeitung',
    description: 'Upload OQOOD or SPA documents. Our AI automatically extracts all property details in seconds.',
    descriptionDe: 'OQOOD oder SPA hochladen. Unsere KI extrahiert automatisch alle Immobiliendetails in Sekunden.',
    icon: <Bot size={24} color="#D6B160" />,
    position: 'bottom'
  },
  {
    id: 'properties-add',
    targetTab: 'properties',
    targetSelector: '[class*="add-property"], button:contains("Add")',
    title: 'Add Property Manually',
    titleDe: 'Immobilie manuell hinzufügen',
    description: 'Prefer to enter details yourself? Add any property manually with full customization.',
    descriptionDe: 'Details selbst eingeben? Fügen Sie jede Immobilie manuell mit voller Anpassung hinzu.',
    icon: <PlusCircle size={24} color="#D6B160" />,
    position: 'bottom'
  },

  // ===== PORTFOLIO TAB =====
  {
    id: 'portfolio-intro',
    targetTab: 'portfolio',
    title: 'Portfolio Analytics',
    titleDe: 'Portfolio-Analyse',
    description: 'Deep insights into your investment performance with real-time valuations.',
    descriptionDe: 'Tiefe Einblicke in Ihre Investitionsperformance mit Echtzeit-Bewertungen.',
    icon: <BarChart3 size={24} color="#D6B160" />
  },
  {
    id: 'portfolio-value',
    targetTab: 'portfolio',
    targetSelector: '[class*="total-value"], [class*="portfolio-value"], .value-card',
    title: 'Total Portfolio Value',
    titleDe: 'Gesamter Portfolio-Wert',
    description: 'Your complete investment value in AED and EUR. Updated with current market rates.',
    descriptionDe: 'Ihr kompletter Investitionswert in AED und EUR. Aktualisiert mit aktuellen Marktkursen.',
    icon: <Banknote size={24} color="#D6B160" />,
    position: 'bottom'
  },
  {
    id: 'portfolio-charts',
    targetTab: 'portfolio',
    targetSelector: '[class*="chart"], [class*="breakdown"], canvas',
    title: 'Investment Breakdown',
    titleDe: 'Investment-Aufschlüsselung',
    description: 'Visual analytics by developer, location, property type, and yearly investment trends.',
    descriptionDe: 'Visuelle Analysen nach Entwickler, Standort, Immobilientyp und jährlichen Investment-Trends.',
    icon: <TrendingUp size={24} color="#D6B160" />,
    position: 'auto'
  },

  // ===== MENU TAB =====
  {
    id: 'menu-intro',
    targetTab: 'menu',
    title: 'Tools & Services',
    titleDe: 'Tools & Services',
    description: 'Access powerful investment tools, community features, and personalized services.',
    descriptionDe: 'Zugang zu leistungsstarken Investment-Tools, Community-Features und personalisierten Services.',
    icon: <Zap size={24} color="#D6B160" />
  },
  {
    id: 'menu-calculator',
    targetTab: 'menu',
    targetSelector: '[class*="roi"], [class*="calculator"]',
    title: 'ROI Calculator',
    titleDe: 'ROI-Rechner',
    description: 'Calculate potential returns, rental yields, and appreciation forecasts before investing.',
    descriptionDe: 'Berechnen Sie potenzielle Renditen, Mietrenditen und Wertsteigerungsprognosen vor dem Investment.',
    icon: <Calculator size={24} color="#D6B160" />,
    position: 'bottom'
  },
  {
    id: 'menu-community',
    targetTab: 'menu',
    targetSelector: '[class*="community"], [class*="whatsapp"]',
    title: 'Investor Community',
    titleDe: 'Investoren-Community',
    description: 'Join exclusive WhatsApp groups with fellow investors. Get construction updates and network.',
    descriptionDe: 'Treten Sie exklusiven WhatsApp-Gruppen bei. Erhalten Sie Bau-Updates und vernetzen Sie sich.',
    icon: <Users size={24} color="#D6B160" />,
    position: 'auto'
  },
  {
    id: 'menu-events',
    targetTab: 'menu',
    targetSelector: '[class*="event"], [class*="webinar"]',
    title: 'Events & Webinars',
    titleDe: 'Events & Webinare',
    description: 'VIP property tours, investor meetups, and exclusive educational webinars.',
    descriptionDe: 'VIP-Immobilienbesichtigungen, Investoren-Treffen und exklusive Bildungswebinare.',
    icon: <Tent size={24} color="#D6B160" />,
    position: 'auto'
  },
  {
    id: 'menu-support',
    targetTab: 'menu',
    targetSelector: '[class*="support"], [class*="profile"]',
    title: 'Premium Support',
    titleDe: 'Premium Support',
    description: 'Direct access to your personal investment advisor and 24/7 concierge support.',
    descriptionDe: 'Direkter Zugang zu Ihrem persönlichen Anlageberater und 24/7 Concierge-Support.',
    icon: <LifeBuoy size={24} color="#D6B160" />,
    position: 'auto'
  },

  // ===== COMPLETE =====
  {
    id: 'complete',
    title: 'You\'re All Set!',
    titleDe: 'Alles bereit!',
    description: 'Start exploring your investment portfolio. Restart this tour anytime from Menu.',
    descriptionDe: 'Erkunden Sie Ihr Investmentportfolio. Tour jederzeit im Menü neu starten.',
    icon: <PartyPopper size={24} color="#D6B160" />
  }
];

// ============================================
// SPOTLIGHT COMPONENT
// ============================================

interface SpotlightProps {
  targetRect: DOMRect | null;
  padding?: number;
}

const Spotlight: React.FC<SpotlightProps> = ({ targetRect, padding = 12 }) => {
  if (!targetRect) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.75)',
        zIndex: 99990
      }} />
    );
  }

  const top = Math.max(0, targetRect.top - padding);
  const left = Math.max(0, targetRect.left - padding);
  const width = targetRect.width + padding * 2;
  const height = targetRect.height + padding * 2;
  const right = left + width;
  const bottom = top + height;

  return (
    <>
      {/* Dark overlay with cutout */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99990,
        pointerEvents: 'none'
      }}>
        {/* Top */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: top,
          background: 'rgba(0,0,0,0.8)'
        }} />
        {/* Bottom */}
        <div style={{
          position: 'absolute',
          top: bottom,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)'
        }} />
        {/* Left */}
        <div style={{
          position: 'absolute',
          top: top,
          left: 0,
          width: left,
          height: height,
          background: 'rgba(0,0,0,0.8)'
        }} />
        {/* Right */}
        <div style={{
          position: 'absolute',
          top: top,
          left: right,
          right: 0,
          height: height,
          background: 'rgba(0,0,0,0.8)'
        }} />
      </div>

      {/* Animated gold border around highlighted element */}
      <div style={{
        position: 'fixed',
        top: top - 3,
        left: left - 3,
        width: width + 6,
        height: height + 6,
        border: '3px solid #D6B160',
        borderRadius: '16px',
        zIndex: 99991,
        pointerEvents: 'none',
        boxShadow: '0 0 0 4px rgba(214,177,96,0.3), 0 0 30px rgba(214,177,96,0.5), inset 0 0 20px rgba(214,177,96,0.1)',
        animation: 'spotlightPulse 2s ease-in-out infinite'
      }} />

      {/* Corner accents */}
      <svg style={{
        position: 'fixed',
        top: top - 8,
        left: left - 8,
        width: width + 16,
        height: height + 16,
        zIndex: 99992,
        pointerEvents: 'none'
      }}>
        {/* Top-left corner */}
        <path d="M 5 20 L 5 5 L 20 5" stroke="#D6B160" strokeWidth="3" fill="none" strokeLinecap="round" />
        {/* Top-right corner */}
        <path d={`M ${width + 11} 5 L ${width + 11} 5 L ${width + 11} 20`} stroke="#D6B160" strokeWidth="3" fill="none" strokeLinecap="round" />
        <path d={`M ${width - 4} 5 L ${width + 11} 5`} stroke="#D6B160" strokeWidth="3" fill="none" strokeLinecap="round" />
        {/* Bottom-left corner */}
        <path d={`M 5 ${height - 4} L 5 ${height + 11} L 20 ${height + 11}`} stroke="#D6B160" strokeWidth="3" fill="none" strokeLinecap="round" />
        {/* Bottom-right corner */}
        <path d={`M ${width - 4} ${height + 11} L ${width + 11} ${height + 11} L ${width + 11} ${height - 4}`} stroke="#D6B160" strokeWidth="3" fill="none" strokeLinecap="round" />
      </svg>
    </>
  );
};

// ============================================
// WALKTHROUGH COMPONENT
// ============================================

interface AppWalkthroughProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  onTabChange?: React.Dispatch<React.SetStateAction<string>> | ((tab: string) => void);
  language?: 'en' | 'de';
}

const AppWalkthrough: React.FC<AppWalkthroughProps> = ({
  isOpen,
  onClose,
  onComplete,
  onTabChange,
  language
}) => {
  // The `language` prop is optional and callers currently omit it, which used to
  // pin the tour to English (default 'en') even for a Deutsch user, hiding the
  // titleDe/descriptionDe texts that already exist below. Fall back to the
  // selected app language instead of hard-coding English.
  const { t, language: appLanguage } = useLanguage();
  const activeLanguage = language || appLanguage;
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const step = tourSteps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === tourSteps.length - 1;
  const totalSteps = tourSteps.length;

  // Reset when opening
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setTargetRect(null);
    }
  }, [isOpen]);

  // Auto-switch tab when step changes
  useEffect(() => {
    if (isOpen && step?.targetTab && onTabChange) {
      onTabChange(step.targetTab);
    }
  }, [isOpen, currentStep, step?.targetTab, onTabChange]);

  // Find and track target element
  useEffect(() => {
    if (!isOpen) return;

    // Small delay to let tab content render
    const timeout = setTimeout(() => {
      if (!step?.targetSelector) {
        setTargetRect(null);
        return;
      }

      const selectors = step.targetSelector.split(', ');
      let element: Element | null = null;

      for (const selector of selectors) {
        try {
          element = document.querySelector(selector);
          if (element) break;
        } catch (e) {
          // Invalid selector, try next
        }
      }

      if (element) {
        const rect = element.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setTargetRect(rect);

          // Scroll element into view if needed
          if (rect.top < 100 || rect.bottom > window.innerHeight - 200) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Update rect after scroll
            setTimeout(() => {
              setTargetRect(element!.getBoundingClientRect());
            }, 300);
          }

          // Track resizes
          if (observerRef.current) observerRef.current.disconnect();
          observerRef.current = new ResizeObserver(() => {
            setTargetRect(element!.getBoundingClientRect());
          });
          observerRef.current.observe(element);
        } else {
          setTargetRect(null);
        }
      } else {
        setTargetRect(null);
      }
    }, 400);

    return () => {
      clearTimeout(timeout);
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [isOpen, currentStep, step?.targetSelector, step?.targetTab]);

  const handleNext = useCallback(() => {
    if (isLast) {
      onComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  }, [isLast, onComplete]);

  const handlePrev = useCallback(() => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  }, []);

  if (!isOpen) return null;

  const title = activeLanguage === 'de' && step.titleDe ? step.titleDe : step.title;
  const description = activeLanguage === 'de' && step.descriptionDe ? step.descriptionDe : step.description;

  // Calculate tooltip position
  const tooltipPosition = step.position || 'auto';
  let tooltipStyle: React.CSSProperties = {
    position: 'fixed',
    left: '12px',
    right: '12px',
    background: 'linear-gradient(145deg, #ffffff 0%, #f8f8f8 100%)',
    borderRadius: '24px',
    padding: '24px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(214,177,96,0.2)',
    fontFamily: "'Montserrat', -apple-system, sans-serif",
    zIndex: 99999,
    maxWidth: '400px',
    margin: '0 auto'
  };

  if (targetRect && tooltipPosition !== 'auto') {
    if (tooltipPosition === 'top' || targetRect.bottom > window.innerHeight / 2) {
      tooltipStyle.top = '100px';
    } else {
      tooltipStyle.bottom = '100px';
    }
  } else if (targetRect) {
    // Auto position
    if (targetRect.top > window.innerHeight / 2) {
      tooltipStyle.top = '100px';
    } else {
      tooltipStyle.bottom = '100px';
    }
  } else {
    // No target - center
    tooltipStyle.top = '50%';
    tooltipStyle.transform = 'translateY(-50%)';
  }

  // Tab ids ('dashboard' | 'properties' | 'portfolio' | 'menu') double as base
  // translation keys, so the badge shows the same label as the tab bar.
  const currentTabName = step.targetTab
    ? t(step.targetTab)
    : null;

  return createPortal(
    <>
      {/* Keyframes */}
      <style>{`
        @keyframes spotlightPulse {
          0%, 100% { 
            box-shadow: 0 0 0 4px rgba(214,177,96,0.3), 0 0 30px rgba(214,177,96,0.4);
          }
          50% { 
            box-shadow: 0 0 0 8px rgba(214,177,96,0.4), 0 0 50px rgba(214,177,96,0.6);
          }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
      `}</style>

      {/* Spotlight overlay */}
      <Spotlight targetRect={targetRect} padding={16} />

      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'fixed',
          top: '16px',
          right: '16px',
          background: 'rgba(255,255,255,0.95)',
          border: 'none',
          borderRadius: '50%',
          width: '44px',
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: '#333',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          zIndex: 100000
        }}
      >
        <X size={22} />
      </button>

      {/* Step indicator - top left */}
      <div style={{
        position: 'fixed',
        top: '16px',
        left: '16px',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%)',
        color: 'white',
        padding: '10px 16px',
        borderRadius: '30px',
        fontSize: '13px',
        fontWeight: '600',
        fontFamily: "'Montserrat', -apple-system, sans-serif",
        zIndex: 100000,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
      }}>
        <Sparkles size={14} color="#D6B160" />
        <span style={{ color: '#D6B160', fontSize: '15px' }}>{currentStep + 1}</span>
        <span style={{ opacity: 0.4 }}>/</span>
        <span style={{ opacity: 0.6 }}>{totalSteps}</span>
        {currentTabName && (
          <>
            <div style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.2)' }} />
            <span style={{ color: '#D6B160', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '1px' }}>
              {currentTabName}
            </span>
          </>
        )}
      </div>

      {/* Tooltip card */}
      <div style={tooltipStyle}>
        {/* Premium gold accent */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, transparent, #D6B160, #ecd9a3, #D6B160, transparent)',
          borderRadius: '24px 24px 0 0',
          backgroundSize: '200% 100%',
          animation: 'shimmer 3s infinite linear'
        }} />

        {/* Icon + Title */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          marginBottom: '12px',
          marginTop: '8px'
        }}>
          {step.icon && (
            <div style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%)',
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
            }}>
              {step.icon}
            </div>
          )}
          <div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#1a1a2e',
              margin: 0,
              lineHeight: 1.2
            }}>
              {title}
            </h3>
            {step.targetSelector && (
              <span style={{
                fontSize: '11px',
                color: '#D6B160',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontWeight: '600'
              }}>
                {t('awtHighlightedAbove')}
              </span>
            )}
          </div>
        </div>
        
        <p style={{
          fontSize: '15px',
          color: '#555',
          lineHeight: '1.7',
          marginBottom: '20px'
        }}>
          {description}
        </p>

        {/* Progress bar */}
        <div style={{
          height: '3px',
          background: 'rgba(0,0,0,0.08)',
          borderRadius: '2px',
          marginBottom: '20px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${((currentStep + 1) / totalSteps) * 100}%`,
            background: 'linear-gradient(90deg, #D6B160, #c4a050)',
            borderRadius: '2px',
            transition: 'width 0.4s ease'
          }} />
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', gap: '12px' }}>
          {!isFirst ? (
            <button
              onClick={handlePrev}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '14px',
                background: '#f0f0f0',
                border: 'none',
                borderRadius: '14px',
                fontSize: '15px',
                fontWeight: '600',
                color: '#333',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <ChevronLeft size={18} /> {t('back')}
            </button>
          ) : (
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '14px',
                background: 'transparent',
                border: '2px solid #e0e0e0',
                borderRadius: '14px',
                fontSize: '15px',
                fontWeight: '600',
                color: '#999',
                cursor: 'pointer'
              }}
            >
              {t('awtSkipTour')}
            </button>
          )}
          
          <button
            onClick={handleNext}
            style={{
              flex: 1.3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '14px',
              background: 'linear-gradient(135deg, #D6B160 0%, #c4a050 100%)',
              border: 'none',
              borderRadius: '14px',
              fontSize: '15px',
              fontWeight: '700',
              color: 'white',
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(214,177,96,0.5)',
              transition: 'all 0.2s'
            }}
          >
            {isLast ? (
              <><Check size={18} /> {t('awtFinish')}</>
            ) : (
              <>{t('awtNext')} <ChevronRight size={18} /></>
            )}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default AppWalkthrough;